import { useState } from 'react'
import Carfax from 'lib/carfax/component'
import Link from 'next/link'
import MoreButton from '@components/display/MoreButton'
import useT from '@hooks/useTranslation'
import createSlug from '@utils/slug/createSlug'
import simpleDate from '@utils/date/simpleDate'
import CAT from '@constants/categories'
import mapSpecValue from './maps/specifications'
import withBreakpoints from '@hoc/withBreakpoints'
import offerDealerOrPrivate from './functions/offerDealerOrPrivate'

import styles from 'styles/OfferSpecifications.module.css'

const LINK_PROP = {
    'Akryl': 'lakier-akrylowy',
    'Perłowy': 'lakier-perlowy',
    'Matowy': 'lakier-matowy',
    'Metalik': 'lakier-metalik',
    'manual': 'skrzynia-biegow-manualna',
    'automatic': 'skrzynia-biegow-automatyczna',
    '4by4': 'naped-4x4',
    'front': 'naped-przednie-kola',
    'back': 'naped-tylne-kola',
}

const Specifications = ({
    breakpoints: { mobile: onMobile },
    buy = false,
    carfax = false,
    carfaxVinData,
    linkBase = {},
    offer = {},
    rent = false,
}) => {

    if (!offer) return null

    const [showSecondary, setShowSecondary] = useState(false)
    const { type, akol = {}, data = {}, moreinfo = {}, price = {}} = offer
    const doLink = (components, params = '') => `${linkBase}/${
        components
            .filter(Boolean)
            .map(v => encodeURIComponent(v))
            .join('/')}` + params


    const isMotorcycle = type === 'motorcycle_ad'
    const isService = type === 'service_ad'

    const makeLink = rent ? linkBase : doLink([offer.make])
    const modelLink = rent
        ? linkBase
        : slugify(doSplModelLink(type, [offer.make, offer.model], doLink))

    const bodyType = data.body_type || data.truck_body_type
    const state = typeof offer._new === 'boolean'
        ? offer._new
        : offer.part_state

    const offerModel = offer.make && offer.model && `${offer.make} ${offer.model}`

    const primarySpec = [
        ['Rodzaj oferty',                      offerKind(data)],
        ['Kategoria',                          CAT[offer._adtype || offer.type],null,  linkBase],
        ['Marka',                              offer.make,                      null,  rent ? `${linkBase}?marka=${offer.make}` : makeLink],
        ['Model',                              offerModel,                      null,  rent ? `${linkBase}?marka=${offer.make}&model=${offer.model}` : modelLink],
    ]

    const specShiftingOnMobile = [
        ['Stan',                               isService
                                                ? null
                                                : state ? 'Nowe' : 'Używane',   null,  `${makeLink}?stan=${state ? 'Nowy' : 'Uzywany'}`],

        ['Pojazd',                             offer.part_vehicle,              null,   `${makeLink}?pojazd=${offer.part_vehicle}`],
        ['Marka',                              offer.part_make,                 null,   `${makeLink}?do-marki=${offer.part_make}`],
        ['Producent części',                   offer.part_producer,             null],
        ['Numer części',                       offer.part_reference_number,     null,   `${makeLink}?numer-referencyjny=${offer.part_reference_number}`],

        ['Dopuszczalna ładowność',             data.load_capacity,              'kg',   `${makeLink}?dopuszczalna-ladownosc-od=${data.load_capacity}`],
        ['Dopuszczalna masa całkowita',        data.total_mass,                 'kg',   `${makeLink}?dopuszczalna-masa-od=${data.total_mass}`],
        ['Kubatura',                           data.capacity,                   'm³',   `${makeLink}?kubatura-od=${data.capacity}`],

        ['Ogłoszenie autoryzowanego dealera',  data.authorized,                 null,  `${makeLink}?autoryzowany-dealer=true`],
        ['Oferta firmowa',                     data.dealer,                     null,  `${makeLink}?oferty-firmowe=true`],
        ['Oferta prywatna',                    data.private,                    null,  `${makeLink}?oferty-prywatne=true`],
        ['Podkategoria pojazdu',               akol.podkategoria_pojazdu,       null,  `${makeLink}?podkategoria-pojazdu=${akol.podkategoria_pojazdu}`],
        ['Napęd',                              akol.naped,                      null,  `${makeLink}?naped=${akol.naped}`],
        ['Wysięgnik',                          akol.wysiegnik,                  null,  `${makeLink}?wysiegnik=${akol.wysiegnik}`],
        ['Lemiesz',                            akol.lemiesz,                    null,  `${makeLink}?lemiesz=${akol.lemiesz}`],
        ['Rok modelowy',                       moreinfo.model_year],
        ['Generacja',                          offer.generation],
        ['Typ',                                bodyType,                        null,  `${makeLink}?${isMotorcycle ? 'typ' : 'typ-nadwozia'}=${bodyType}`],
        ['Wersja wyposażenia',                 data.equipment],
        ['Silnik',                             offer.trim],
        ['Pojemność silnika',                  data.displacement_capacity,      'cm³', `${makeLink}?pojemnosc-od=${data.displacement_capacity}`],
        ['Moc silnika',                        data.engine_power,               'KM',  `${makeLink}?moc-silnika-od=${data.engine_power}`],
        ['Skrzynia biegów',                    data.gearbox,                    null,  `${makeLink}?${LINK_PROP[data.gearbox]}=true`],
        ['Motogodziny',                        offer.motohours,                 'h'],
        ['Napęd',                              data.drive,                      null,  `${makeLink}?${LINK_PROP[data.drive]}=true`],
        ['Rodzaj napędu',                      data.drive_4by4_type],
        ['Klimatyzacja',                       data.air_conditioning],
        ['Rodzaj klimatyzacji',                data.automatic_air_conditioning_type],
        ['Kolor',                              offer.color,                     null,  `${makeLink}?kolor=kolor-${createSlug(offer.color)}`],
        ['Rodzaj lakieru',                     data.lacquer,                    null,  `${makeLink}?${LINK_PROP[data.lacquer]}=true`],
        ['Możliwość finansowania',             data.financing,                  null,  `${makeLink}?finansowanie=true`],
        ['Faktura VAT',                        offer.price?.price_invoice,      null,  `${makeLink}?faktura-vat=true`],
        ['Status sprowadzonego',               akol.status_sprowadzonego],
        ['Kraj pochodzenia',                   data.country_of_origin,          null,  `${makeLink}?kraj-pochodzenia=${data.country_of_origin}`],
        ['Kraj rejestracji',                   data.registered_in],
        ['Poleasingowy',                       data.after_leasing,              null,  `${makeLink}?typ-nadwozia=Poleasingowy`],
        ['Pierwszy właściciel',                data.first_owner,                null,  `${makeLink}?pierwszy-wlasciciel=true`],
        ['Homologacja ciężarowa',              data.truck_approval,             null,  `${makeLink}?z-homologacja-ciezarowa=true`],
        ['Data pierwszej rejestracji',         simpleDate(moreinfo.first_registration_date)],
        ['Numer rejestracyjny',                data.registration_number],
        ['VIN',                                offer.vin],
    ]

    const buySpec = buy && [
        ['Kategoria',                          offer.category],
        ['Podkategoria',                       offer.subcategory],
        ['Rodzaj',                             offer.kind],
        ['Rodzaj',                             offer.part_kind],
        ['Nazwa',                              offer.part_name],
    ]

    const secondarySpec = [
        ['Liczba drzwi',                       data.doors,                         null,  `${makeLink}?liczba-drzwi-od=${data.doors}`],
        ['Liczba miejsc',                      data.seats,                         null,  `${makeLink}?liczba-miejsc-od=${data.seats}`],
        ['Audio',                              akol.audio],
        ['Kierownica po prawej stronie',       data.steeringwheel,                 null,  `${makeLink}?kierownica-po-prawej=true`],
        ['Szerokość lemiesza',                 akol.szerokosc_lemiesza_w_m,        'm'],
        ['Liczba silników',                    akol.liczba_silnikow],
        ['Długość',                            akol.dlugosc_w_cm,                  'cm'],
        ['Szerokość',                          akol.szerokosc_w_cm,                'cm'],
        ['Liczba osi',                         akol.liczba_osi],
        ['Liczba łopat silnika',               akol.liczba_lopat_silnika],
        ['Wysokość podnoszenia',               akol.wysokosc_podnoszenia_w_cm,     'cm'],
        ['Waga',                               akol.waga_w_tonach,                 'T'],
        ['Udźwig',                             akol.udzwig_w_kg,                   'kg'],
        ['Motogodziny do przeglądu',           akol.motogodziny_do_przegladu,      'h'],
        ['Pojemność łyżki',                    akol.pojemnosc_lyzki_w_m3,          'm³'],
        ['Masa eksploatacyjna',                akol.masa_eksploatacyjna_w_tonach,  'T'],
        ['Moc użyteczna',                      akol.moc_uzyteczna_w_KM,            'KM'],
        ['Szerokość gąsienic',                 akol.szerokosc_gasienic_w_mm,       'mm'],
        ['Głębokość kopania',                  akol.glebokosc_kopania_w_m,         'm'],
        ['Moc agregatu',                       akol.moc_agregatu_w_kVA,            'kVA'],
        ['Historia serwisu w ASO',             data.ASO_history,                   null,  `${makeLink}?z-historia-aso=true`],
        ['Bezwypadkowy',                       data.noaccident,                    null,  `${makeLink}?bezwypadkowe=true`],
        ['Uszkodzony',                         data.damaged,                       null,  `${makeLink}?uszkodzone=true`],
        ['Zabytkowy',                          data.relic,                         null,  `${makeLink}?zabytkowe=true`],
        ['Paliwo E10',                         akol.paliwo_e10],
        ['Biodiesel',                          akol.biodiesel],
        ['Olej roślinny',                      akol.olej_roslinny],
        ['AdBlue',                             data.adblue],
        ['Filtr cząstek stałych',              data.particlefilter],
        ['Pojazd nienormatywny',               akol.pojazd_nienormatywny],

        //
        ['Cesja',                              data.transfer],
        ['Cena do negocjacji',                 price.price_is_negotiable],
        ['VAT marża',                          data.vatmargin],
        ['Podwójne tylne koła',                data.double_wheels],
        ['Agregat chłodzący',                  data.cooling_aggregate],
        ['Emisja CO2',                         data.co2_emission,                  'g/km'],
        //

        ['Data dodania',                       simpleDate(offer.created)],
    ]

    const usablePrimarySpec = onMobile
        ? primarySpec
        : [...primarySpec, ...specShiftingOnMobile]

    const usableSecondarySpec = onMobile
        ? [...specShiftingOnMobile, ...secondarySpec]
        : secondarySpec

    const viewableSecondarySpec = usableSecondarySpec
        .filter(([,value]) => value != null)

    const hasSecondarySpec = viewableSecondarySpec.length > 0
    const showSecondarySpec = () => setShowSecondary(true)

    return (
        <div className={styles.specs}>
            <div>
                <List items={usablePrimarySpec} />
                { buy &&
                    <List items={buySpec} /> }
            </div>
            {
                hasSecondarySpec && <div>
                    { showSecondary
                        ? <List items={viewableSecondarySpec} />
                        : <MoreButton onClick={showSecondarySpec} /> }
                </div>
            }

            { carfax && offer.make && offer.model &&
                <Carfax carfaxVinData={carfaxVinData} key={offer.vin || offer._id} offer={offer} /> }
        </div>
    )
}

function List ({ items }) {
    return items.map((item, i) => <Spec spec={item} key={`key_${i}`}/>)
}

function Spec ({ spec }) {
    let [name, value, unit, link] = spec

    if (value == null || value == '') return null

    value = mapSpecValue(name, value)

    const tName = useT(name)
    const tValue = typeof value === 'boolean'
        ? value ? useT('Tak') : useT('Nie')
        : isNaN(value)
            ? useT(value)
            : value

    const valueComponent = <>{tValue} {unit}</>
    return (
        <div className={styles.spec}>
            <span className={styles.name}>{tName}</span>
            <span className={styles.value}>
                { link
                    ? <Link href={link}><a>
                        {valueComponent}
                    </a></Link>

                    : valueComponent }
            </span>
        </div>
    )
}

function offerKind (data) {
    const { priv, dealer } = offerDealerOrPrivate(data)
    if (dealer) return 'Oferta firmowa'
    if (priv) return 'Oferta prywatna'
}

function doSplModelLink (type, makeModel, doLink) {
    const base = 'https://samochody.pl'

    switch (type) {
        case 'truck_ad': return [base,'dostawcze',...makeModel].join('/')
        case 'motorcycle_ad': return [base,'motocykle',...makeModel].join('/')
        case 'ciezarowe': return [base,'samochody-ciezarowe',...makeModel].join('/')
        case 'przyczepy': return [base,'przyczepy',...makeModel].join('/')
        case 'budowlane': return [base,'maszyny-budowlane-sprzedaz',...makeModel].join('/')
        case 'agri_ad': return [base,'maszyny-rolnicze-sprzedaz',...makeModel].join('/')

        default: return doLink(makeModel)
    }
}

const charsOkInComponent = ['(',')','~',',','.',':','_','/']
const charsOkInUrl = ['?','=','&', ...charsOkInComponent]

function slugify (url) {
    return createSlug(decodeURIComponent(url), { additionalyAllowed: charsOkInUrl })
}

export default withBreakpoints({ mobile: 800 })(Specifications)